import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchResultItem, AllApps } from "./types";
import { ALL_APPS, MOCK } from './constants';
import { AppIcon, TypeIcon } from './icons';
import { clamp, highlight } from './utils/helpers';
import { mockSearch, Filters } from './services/searchService';
import { ResultCard } from './ResultCard';
import { FacetSelect } from './FacetSelect';
import './GlobalSearch.css';

export interface GlobalSearchProps {
  placeholder?: string;
  onSearchChange?: (query: string) => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ placeholder = "Search — try: MS020, JG-TE-1402, TP-JG-AC-087, PO-JG-7742", onSearchChange }) => {
  const [query, setQuery] = useState("");
  const [activeApp, setActiveApp] = useState<AllApps>("All Apps");
  const [sort, setSort] = useState("Relevance");
  const [filters, setFilters] = useState<Filters>({
    app: "All Apps",
    project: "All",
    type: "All",
    discipline: "All",
    status: "All",
    updated: null,
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mode, setMode] = useState("home"); // home = project gallery; results = full search page

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(query);
    }
  }, [query, onSearchChange]);

  const projects = useMemo(() => ["All", ...Array.from(new Set(MOCK.map((m) => m.project))).filter(Boolean)], []);
  const types = useMemo(() => ["All", ...Array.from(new Set(MOCK.map((m) => m.type))).filter(Boolean)], []);
  const disciplines = useMemo(() => ["All", ...Array.from(new Set(MOCK.map((m) => m.discipline))).filter(Boolean)], []);
  const statuses = useMemo(() => ["All", ...Array.from(new Set(MOCK.map((m) => m.status))).filter(Boolean)], []);

  const results = useMemo(() => mockSearch({ query, activeApp, filters, sort }).map((x) => ({ ...x, _q: query })), [query, activeApp, filters, sort]);

  const countsByApp = useMemo(() => {
    const base = Object.fromEntries(ALL_APPS.map((a) => [a, 0])) as Record<AllApps, number>;
    const all = mockSearch({ query, activeApp: "All Apps", filters, sort });
    for (const it of all) {
        if (base.hasOwnProperty(it.app)) {
            base[it.app] = (base[it.app] || 0) + 1;
        }
    }
    base["All Apps"] = all.length;
    return base;
  }, [query, filters, sort]);

  const groupedSuggestions = useMemo(() => {
    if (!query) return {};
    const r = mockSearch({ query, activeApp: "All Apps", filters: { ...filters, updated: null }, sort: "Relevance" }).slice(0, 60).map((x) => ({ ...x, _q: query }));
    const g: { [key: string]: SearchResultItem[] } = {};
    for (const it of r) {
      if (!g[it.app]) g[it.app] = [];
      if (g[it.app].length < 3) g[it.app].push(it);
    }
    return g;
  }, [query, filters]);

  const flatSuggestions = useMemo(() => Object.values(groupedSuggestions).flat(), [groupedSuggestions]);
  
  const visibleSuggestions = useMemo(() => {
    if (!query) return [];
    return flatSuggestions.slice(0, 8);
  }, [query, flatSuggestions]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
        setShowSuggestions(true);
      } else if (e.key === "Escape") {
        setQuery("");
        setActiveIndex(-1);
        setShowSuggestions(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => clamp(i + 1, -1, visibleSuggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => clamp(i - 1, -1, visibleSuggestions.length - 1));
      } else if (e.key === "Enter") {
        if (activeIndex >= 0 && visibleSuggestions[activeIndex]) {
          onOpenResult(visibleSuggestions[activeIndex]);
        } else if (query) {
          setMode("results");
          setShowSuggestions(false);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSuggestions, activeIndex]);

  const clearFilters = () => setFilters({ app: "All Apps", project: "All", type: "All", discipline: "All", status: "All", updated: null });
  const onOpenResult = (item: SearchResultItem) => window.alert(`Open → ${item.title} ( ${item.url} )`);

  return (
    <>
      <div className="kxd-global-search-container">
        <div className="kxd-search-bar-wrapper">
          <div className="kxd-search-input-container">
            <svg 
              className="kxd-search-icon" 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => { if (query) { setMode("results"); setShowSuggestions(false); } }}
              style={{ cursor: query ? 'pointer' : 'default' }}
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
            </svg>
            <input 
              ref={inputRef} 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              onFocus={() => setShowSuggestions(true)} 
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !activeIndex && query) {
                  setMode("results");
                  setShowSuggestions(false);
                }
              }}
              placeholder={placeholder} 
              className="kxd-search-input"
            />
            {query && (
              <button onClick={() => setQuery("")} className="kxd-search-clear-btn">Clear</button>
            )}

            {showSuggestions && query && (
              <div className="kxd-suggestions-dropdown" onMouseDown={(e) => e.preventDefault()}>
                {/* Suggestions List */}
                <div className="kxd-suggestions-list">
                  {visibleSuggestions.length === 0 ? (
                    <div className="kxd-no-results">No results found</div>
                  ) : (
                    visibleSuggestions.map((it, idx) => (
                      <div 
                        key={it.id} 
                        className={`kxd-suggestion-item ${idx === activeIndex ? "active" : ""}`} 
                        onMouseEnter={() => setActiveIndex(idx)} 
                        onClick={() => { onOpenResult(it); setShowSuggestions(false); }}
                      >
                        <span className="kxd-suggestion-icon">{TypeIcon[it.type]}</span>
                        <div className="kxd-suggestion-content">
                          <div className="kxd-suggestion-title">{highlight(it.title, query)}</div>
                          <div className="kxd-suggestion-breadcrumb">{it.breadcrumb}</div>
                        </div>
                        <span className="kxd-suggestion-badge">{it.project || "Global"}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Go to all sections */}
                <div className="kxd-goto-sections">
                  {ALL_APPS.filter(app => app !== 'All Apps' && (groupedSuggestions[app]?.length || 0) > 0).map((app) => (
                    <button 
                      key={app}
                      className="kxd-goto-item"
                      onClick={() => { setActiveApp(app as AllApps); setMode("results"); setShowSuggestions(false); }}
                    >
                      <span className="kxd-goto-icon">{AppIcon[app]}</span>
                      <span className="kxd-goto-text">Go to all</span>
                      <span className="kxd-goto-label">{app}</span>
                      <span className="kxd-goto-count">({countsByApp[app as AllApps] || 0})</span>
                    </button>
                  ))}
                </div>

                {/* Footer hints */}
                <div className="kxd-suggestions-footer">
                  <span className="kxd-footer-hint">
                    <kbd>↑</kbd> <kbd>↓</kbd> to navigate
                  </span>
                  <span className="kxd-footer-hint">
                    <kbd>↵</kbd> to select
                  </span>
                  <span className="kxd-footer-hint">
                    <kbd>esc</kbd> to dismiss
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {mode === "results" && (
        <div className="kxd-search-results-overlay" onClick={() => setMode("home")}>
          <div className="kxd-search-results-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kxd-search-results-header">
              <div className="kxd-modal-search-container">
                <svg className="kxd-modal-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
                </svg>
                <input 
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search — try: MS020, JG-TE-1402, TP-JG-AC-087, PO-JG-7742"
                  className="kxd-modal-search-input"
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="kxd-search-sort-select">
                  <option>Relevance</option><option>Recent</option><option>Popular</option>
                </select>
                <button onClick={() => setMode("home")} className="kxd-close-results-btn">✕</button>
              </div>
            </div>
            <div className="kxd-search-results-content">
              <div className="kxd-results-layout">
                <div className="kxd-filters-sidebar">
                  <div className="kxd-filters-panel">
                    <div className="kxd-filters-header">
                      <h2 className="kxd-filters-title">Filters</h2>
                      <button className="kxd-filters-reset" onClick={clearFilters}>Reset</button>
                    </div>
                    <FacetSelect label="Application" value={filters.app} onChange={(v) => setFilters((f) => ({ ...f, app: v as AllApps }))} options={ALL_APPS} />
                    <FacetSelect label="Project" value={filters.project} onChange={(v) => setFilters((f) => ({ ...f, project: v }))} options={projects} />
                    <FacetSelect label="Type" value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} options={types} />
                    <FacetSelect label="Discipline" value={filters.discipline} onChange={(v) => setFilters((f) => ({ ...f, discipline: v }))} options={disciplines} />
                    <FacetSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={statuses} />

                    <div style={{ fontSize: '0.875rem', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <span>Updated</span>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className="kxd-action-btn kxd-btn-gray" onClick={() => setFilters((f) => ({ ...f, updated: { from: new Date(Date.now() - 7 * 86400000).toISOString(), to: null } }))}>7d</button>
                        <button className="kxd-action-btn kxd-btn-gray" onClick={() => setFilters((f) => ({ ...f, updated: { from: new Date(Date.now() - 30 * 86400000).toISOString(), to: null } }))}>30d</button>
                        <button className="kxd-action-btn kxd-btn-gray" onClick={() => setFilters((f) => ({ ...f, updated: null }))}>Any</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="kxd-results-main">
                  <div className="kxd-app-filters">
                    {ALL_APPS.map((app) => (
                      <button 
                        key={app} 
                        onClick={() => setActiveApp(app)} 
                        className={`kxd-app-filter-btn ${activeApp === app ? "active" : ""}`}
                      >
                        <span className="kxd-suggestion-icon">{AppIcon[app === 'All Apps' ? 'Core' : app]}</span>
                        {app}
                        <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>{countsByApp[app] ?? 0}</span>
                      </button>
                    ))}
                  </div>

                  {activeApp === "All Apps" ? (
                    <div>
                      {ALL_APPS.filter((a) => a !== "All Apps").map((appKey) => {
                        const list = results.filter((r) => r.app === appKey).slice(0, 3);
                        if (list.length === 0) return null;
                        return (
                          <section key={appKey} className="kxd-results-section">
                            <div className="kxd-section-header">
                              <h3 className="kxd-section-title">
                                <span className="kxd-section-icon">{AppIcon[appKey]}</span>
                                {appKey}
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#64748b', fontWeight: 'normal' }}>Top results</span>
                              </h3>
                              <button className="kxd-view-all-btn" onClick={() => setActiveApp(appKey as AllApps)}>View all ({countsByApp[appKey as AllApps] || 0})</button>
                            </div>
                            <div className="kxd-results-grid">{list.map((it) => <ResultCard key={it.id} item={it} onOpen={onOpenResult} />)}</div>
                          </section>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="kxd-results-grid">
                      {results.length === 0 && <div style={{ color: '#64748b', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>No results match your query and filters.</div>}
                      {results.map((it) => <ResultCard key={it.id} item={it} onOpen={onOpenResult} />)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
